import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, authHeaders } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Users, Search, Send, Loader2, MessageSquare, Gift, Ticket, ChevronRight, Ban, BellOff, Bell } from "lucide-react";
import { relativeDate } from "@/lib/format";
import { couponBadge } from "@/pages/vendor/VendorCoupons";
import { toast } from "sonner";

const tplLabel = (t) => `${t.title || "Coupon"} · ${couponBadge(t)}`;

export default function VendorFollowers() {
  const navigate = useNavigate();
  const [followers, setFollowers] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [target, setTarget] = useState(null); // follower being messaged
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [tplCoupon, setTplCoupon] = useState("none");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, t] = await Promise.all([
        api.get("/vendor/followers", { headers: authHeaders("vendor") }),
        api.get("/vendor/coupon-templates", { headers: authHeaders("vendor") }),
      ]);
      setFollowers(r.data.followers || []);
      setCount(r.data.count || 0);
      setTemplates(t.data.templates || []);
    } catch (_) { toast.error("Errore nel caricamento"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = followers.filter((f) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return (f.name || "").toLowerCase().includes(s) || (f.city || "").toLowerCase().includes(s);
  });

  const openMessage = (f) => { setTarget(f); setText(""); setTplCoupon("none"); };

  const setFlag = async (f, kind) => {
    const current = kind === "block" ? f.blocked : f.muted;
    const value = !current;
    setFollowers((xs) => xs.map((x) => x.client_account_id === f.client_account_id ? { ...x, [kind === "block" ? "blocked" : "muted"]: value } : x));
    try {
      await api.post(`/vendor/followers/${f.client_account_id}/${kind}`, { value }, { headers: authHeaders("vendor") });
      toast.success(kind === "block" ? (value ? `${f.name} bloccato` : `${f.name} sbloccato`) : (value ? `${f.name} escluso dai messaggi` : `${f.name} riattivato`));
    } catch (err) {
      toast.error("Errore");
      load();
    }
  };

  const sendMessage = async () => {
    if (!text.trim() && tplCoupon === "none") return toast.error("Scrivi un messaggio o allega un coupon");
    setSending(true);
    try {
      await api.post(`/vendor/followers/${target.client_account_id}/message`, { text: text.trim(), coupon_template_id: tplCoupon === "none" ? null : tplCoupon }, { headers: authHeaders("vendor") });
      toast.success(`Messaggio inviato a ${target.name}`);
      setTarget(null);
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore invio"); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-6 kn-fade-up max-w-3xl" data-testid="vendor-followers-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">I tuoi follower</h1>
          <p className="text-slate-500">I clienti che seguono il tuo negozio.</p>
        </div>
        <div className="flex items-center gap-2 px-3 h-10 rounded-full bg-[#FFE4E6] text-[#BE123C] font-bold text-sm shrink-0" data-testid="followers-count">
          <Users className="h-4 w-4" /> {count} follower
        </div>
      </div>

      <button onClick={() => navigate("/vendor/coupons")} data-testid="coupons-link"
        className="w-full kn-card p-4 flex items-center gap-3 text-left hover:ring-1 hover:ring-[#E63946]/30 transition">
        <div className="h-11 w-11 rounded-xl bg-[#FFE4E6] flex items-center justify-center shrink-0"><Gift className="h-5 w-5 text-[#E63946]" /></div>
        <div className="min-w-0 flex-1">
          <p className="font-bold text-slate-800">Coupon predefiniti</p>
          <p className="text-xs text-slate-400">{templates.length}/10 · crea e gestisci i coupon da allegare ai messaggi</p>
        </div>
        <ChevronRight className="h-5 w-5 text-slate-300 shrink-0" />
      </button>

      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome, email o città..." className="h-12 pl-11 rounded-xl" data-testid="followers-search" />
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#E63946]" /></div>
      ) : count === 0 ? (
        <div className="kn-card p-10 text-center" data-testid="followers-empty">
          <div className="h-14 w-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-3"><Users className="h-7 w-7 text-slate-400" /></div>
          <p className="font-bold text-slate-700">Ancora nessun follower</p>
          <p className="text-sm text-slate-500 mt-1">Pubblica offerte interessanti: i clienti che ti seguono riceveranno i tuoi messaggi e le novità.</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="kn-card p-8 text-center text-slate-400">Nessun follower corrisponde alla ricerca.</div>
      ) : (
        <div className="space-y-2">
          {filtered.map((f) => (
            <div key={f.client_account_id} className="kn-card p-3.5 flex items-center gap-3" data-testid={`follower-${f.client_account_id}`}>
              <div className="h-11 w-11 rounded-full bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center shrink-0">
                {f.photo ? <img src={f.photo} alt="" className="h-full w-full object-cover" /> : <span className="font-extrabold text-slate-400">{(f.name || "?").charAt(0).toUpperCase()}</span>}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-bold text-slate-800 truncate flex items-center gap-1.5">
                  {f.name}
                  {f.blocked && <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded-full bg-red-100 text-[#DC2626]" data-testid={`follower-blocked-${f.client_account_id}`}>BLOCCATO</span>}
                  {!f.blocked && f.muted && <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700" data-testid={`follower-muted-${f.client_account_id}`}>MUTO</span>}
                </p>
                <p className="text-xs text-slate-400 truncate">{f.city ? `${f.city}` : ""}{f.city && f.since ? " · " : ""}{f.since ? `cliente da ${relativeDate(f.since)}` : ""}</p>
              </div>
              <div className="flex items-center gap-0.5 shrink-0">
                <button onClick={() => openMessage(f)} disabled={f.blocked || f.muted} title={f.blocked || f.muted ? "Follower escluso dai messaggi" : "Invia messaggio"} data-testid={`follower-message-${f.client_account_id}`}
                  className="h-9 w-9 rounded-lg flex items-center justify-center text-[#E63946] hover:bg-[#FFF1F2] disabled:opacity-30 disabled:cursor-not-allowed"><MessageSquare className="h-4 w-4" /></button>
                <button onClick={() => setFlag(f, "mute")} title={f.muted ? "Riattiva messaggi" : "Escludi dai messaggi"} data-testid={`follower-mute-${f.client_account_id}`}
                  className={`h-9 w-9 rounded-lg flex items-center justify-center hover:bg-slate-100 ${f.muted ? "text-amber-600" : "text-slate-400"}`}>{f.muted ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}</button>
                <button onClick={() => setFlag(f, "block")} title={f.blocked ? "Sblocca" : "Blocca follower"} data-testid={`follower-block-${f.client_account_id}`}
                  className={`h-9 w-9 rounded-lg flex items-center justify-center hover:bg-red-50 ${f.blocked ? "text-[#DC2626]" : "text-slate-400"}`}><Ban className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!target} onOpenChange={(v) => { if (!v) setTarget(null); }}>
        <DialogContent className="sm:max-w-md rounded-3xl" data-testid="follower-message-dialog">
          <DialogHeader><DialogTitle className="text-xl font-extrabold">Messaggio a {target?.name}</DialogTitle>
            <DialogDescription className="text-slate-500">Scrivi un messaggio e, se vuoi, allega un coupon predefinito.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3.5">
            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} maxLength={1000}
              placeholder="Scrivi un messaggio personale a questo cliente..." className="rounded-xl" data-testid="follower-message-text" />
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1.5"><Ticket className="h-3.5 w-3.5" /> Allega un coupon (facoltativo)</Label>
              <Select value={tplCoupon} onValueChange={setTplCoupon}>
                <SelectTrigger className="h-11 rounded-xl" data-testid="follower-coupon-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessun coupon</SelectItem>
                  {templates.map((t) => <SelectItem key={t.id} value={t.id}>{tplLabel(t)}</SelectItem>)}
                </SelectContent>
              </Select>
              {templates.length === 0 && <button type="button" onClick={() => navigate("/vendor/coupons")} className="text-[11px] text-[#E63946] font-semibold underline">Crea prima un coupon predefinito</button>}
            </div>
            <Button onClick={sendMessage} disabled={sending || (!text.trim() && tplCoupon === "none")} data-testid="follower-message-send" className="w-full h-12 rounded-xl font-bold gap-2">
              {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />} Invia messaggio
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
